import React, { ReactNode } from "react";
import { Box, Button, Typography, useTheme, useMediaQuery } from "@mui/material";
import { SxProps } from "@mui/system";

interface ViewMoreListProps {
  children: ReactNode;
  onViewMore: () => void;
  title: string;
  maxHeight?: string;
}

// Style variables
const styles = {
  container: {
    width: "100%",
    margin: "auto",
    border: "1px solid var(--neutral-100)",
    padding: { xs: "16px", sm: "24px" },
    borderRadius: "1rem",
    overflow: "hidden",
    position: "relative",
    paddingBottom: { xs: "40px", sm: "48px" },
  } as SxProps,

  titleText: {
    fontWeight: 700,
    fontSize: { xs: "18px", sm: "20px" },
    lineHeight: { xs: "24px", sm: "28px" },
    color: "var(--neutral-900)",
    paddingBottom: { xs: "16px", sm: "24px" },
  } as SxProps,

  viewMoreContainer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: { xs: "4px", sm: "16px" },
    paddingTop: { xs: "40px", sm: "48px" },
    background: "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  } as SxProps,

  viewMoreButton: {
    display: "block",
    background: "var(--neutral-50)",
    color: "var(--neutral-900)",
    fontWeight: 500,
    textTransform: "none",
    border: "1px solid var(--neutral-200)",
    borderRadius: "8px",
    fontSize: { xs: "13px", sm: "14px" },
    lineHeight: { xs: "15px", sm: "16px" },
    padding: { xs: "6px 12px", sm: "8px 16px" },
    minWidth: { xs: "100px", sm: "120px" },
  } as SxProps,
} as const;

export default function ViewMoreList({
  children,
  onViewMore,
  title,
  maxHeight,
}: ViewMoreListProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ ...styles.container, maxHeight }}>
      <Typography variant="h6" sx={styles.titleText}>
        {title}
      </Typography>
      
      {children}
      
      <Box sx={styles.viewMoreContainer}>
        <Button
          onClick={onViewMore}
          variant="contained"
          sx={styles.viewMoreButton}
        >
          View More
        </Button>
      </Box>
    </Box>
  );
}
